import type { ActivityContext, ActivityHandle } from '../../core/activity';
import levelsData from './levels.json';

type Mode = 'findDay' | 'findDayOfWeek' | 'findMonthOnly' | 'findYearMonthDay';

interface Config {
  navigationBarVisible: boolean;
  visibleMonth: number;
  visibleYear: number;
  mode: Mode;
  minYear?: number;
  minMonth?: number;
  maxYear?: number;
  maxMonth?: number;
}

interface DayAnswer { year: number; month: number; day: number }
interface DowAnswer { dayOfWeek: number }
interface MonthAnswer { month: number[] }
type Answer = DayAnswer | DowAnswer | MonthAnswer;

interface Question {
  question: string;
  answer: Answer;
}

interface Level {
  level: number;
  config: Config;
  questions: Question[];
}

interface LevelsFile {
  levels: Level[];
}

const LEVELS = (levelsData as LevelsFile).levels;

const MONTHS_PT = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const DOW_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const DOW_LONG = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function firstDow(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

export function mountCalendar(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  const totalQuestions = level.questions.length;
  let qIdx = 0;
  let correctCount = 0;
  let visibleMonth = level.config.visibleMonth;
  let visibleYear = level.config.visibleYear;
  let selectedDay: { year: number; month: number; day: number } | null = null;
  let selectedDow: number | null = null;
  let buttonsBlocked = false;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Calendário</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage cal">
      <div class="feedback" aria-live="polite"></div>
      <p class="cal__progress" aria-live="polite"></p>
      <div class="cal__question" aria-live="polite"></div>
      <div class="cal__nav"></div>
      <div class="cal__grid"></div>
      <div class="cal__dow-choices"></div>
      <div class="cal__actions">
        <button class="btn btn--primary cal__confirm" type="button">Confirmar</button>
      </div>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.cal__progress')!;
  const questionEl = page.querySelector<HTMLElement>('.cal__question')!;
  const navEl = page.querySelector<HTMLElement>('.cal__nav')!;
  const gridEl = page.querySelector<HTMLElement>('.cal__grid')!;
  const dowChoicesEl = page.querySelector<HTMLElement>('.cal__dow-choices')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const confirmBtn = page.querySelector<HTMLButtonElement>('.cal__confirm')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());
  confirmBtn.addEventListener('click', validate);

  function startQuestion(): void {
    visibleMonth = level.config.visibleMonth;
    visibleYear = level.config.visibleYear;
    selectedDay = null;
    selectedDow = null;
    buttonsBlocked = false;
    renderAll();
  }

  function renderAll(): void {
    renderProgress();
    renderQuestion();
    renderNav();
    renderGrid();
    renderDowChoices();
  }

  function renderProgress(): void {
    progressEl.textContent = `Pergunta ${qIdx + 1} de ${totalQuestions}`;
  }

  function renderQuestion(): void {
    questionEl.innerHTML = level.questions[qIdx]!.question;
  }

  function renderNav(): void {
    navEl.innerHTML = '';
    const cfg = level.config;
    if (cfg.navigationBarVisible) {
      const prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'cal__nav-btn';
      prev.textContent = '◀';
      prev.setAttribute('aria-label', 'Mês anterior');
      prev.disabled = !canGoPrev();
      prev.addEventListener('click', () => { if (canGoPrev()) { goPrev(); renderAll(); } });
      navEl.appendChild(prev);
    }
    const title = document.createElement('div');
    title.className = 'cal__nav-title';
    title.textContent = `${MONTHS_PT[visibleMonth]} ${visibleYear}`;
    navEl.appendChild(title);
    if (cfg.navigationBarVisible) {
      const next = document.createElement('button');
      next.type = 'button';
      next.className = 'cal__nav-btn';
      next.textContent = '▶';
      next.setAttribute('aria-label', 'Próximo mês');
      next.disabled = !canGoNext();
      next.addEventListener('click', () => { if (canGoNext()) { goNext(); renderAll(); } });
      navEl.appendChild(next);
    }
  }

  function canGoPrev(): boolean {
    const cfg = level.config;
    if (cfg.minYear === undefined || cfg.minMonth === undefined) return true;
    if (visibleYear > cfg.minYear) return true;
    if (visibleYear === cfg.minYear && visibleMonth > cfg.minMonth) return true;
    return false;
  }
  function canGoNext(): boolean {
    const cfg = level.config;
    if (cfg.maxYear === undefined || cfg.maxMonth === undefined) return true;
    if (visibleYear < cfg.maxYear) return true;
    if (visibleYear === cfg.maxYear && visibleMonth < cfg.maxMonth) return true;
    return false;
  }
  function goPrev(): void {
    visibleMonth--;
    if (visibleMonth < 0) { visibleMonth = 11; visibleYear--; }
  }
  function goNext(): void {
    visibleMonth++;
    if (visibleMonth > 11) { visibleMonth = 0; visibleYear++; }
  }

  function renderGrid(): void {
    gridEl.innerHTML = '';
    // Cabeçalho dias semana
    for (const d of DOW_SHORT) {
      const cell = document.createElement('div');
      cell.className = 'cal__dow';
      cell.textContent = d;
      gridEl.appendChild(cell);
    }
    const dim = daysInMonth(visibleYear, visibleMonth);
    const offset = firstDow(visibleYear, visibleMonth);
    // Células vazias antes do dia 1
    for (let i = 0; i < offset; i++) {
      const cell = document.createElement('div');
      cell.className = 'cal__day cal__day--empty';
      gridEl.appendChild(cell);
    }
    // Dias do mês
    for (let d = 1; d <= dim; d++) {
      const cell = document.createElement('div');
      cell.className = 'cal__day';
      cell.textContent = String(d);
      const isSelected = selectedDay && selectedDay.year === visibleYear && selectedDay.month === visibleMonth && selectedDay.day === d;
      if (isSelected) cell.classList.add('cal__day--selected');
      cell.addEventListener('click', () => onDayClick(d));
      gridEl.appendChild(cell);
    }
  }

  function renderDowChoices(): void {
    dowChoicesEl.innerHTML = '';
    if (level.config.mode !== 'findDayOfWeek') return;
    for (let i = 0; i < 7; i++) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cal__dow-choice';
      if (selectedDow === i) btn.classList.add('cal__dow-choice--selected');
      btn.textContent = DOW_LONG[i]!;
      btn.addEventListener('click', () => {
        if (buttonsBlocked) return;
        selectedDow = i;
        ctx.audio.play('click');
        renderDowChoices();
      });
      dowChoicesEl.appendChild(btn);
    }
  }

  function onDayClick(d: number): void {
    if (buttonsBlocked) return;
    if (level.config.mode === 'findDayOfWeek') return; // sem seleção de dia neste modo
    selectedDay = { year: visibleYear, month: visibleMonth, day: d };
    ctx.audio.play('click');
    renderGrid();
  }

  function validate(): void {
    if (buttonsBlocked) return;
    const q = level.questions[qIdx]!;
    const mode = level.config.mode;
    let ok = false;
    if (mode === 'findDay') {
      const a = q.answer as DayAnswer;
      ok = !!selectedDay && selectedDay.year === a.year && selectedDay.month === a.month && selectedDay.day === a.day;
    } else if (mode === 'findYearMonthDay') {
      const a = q.answer as DayAnswer;
      ok = !!selectedDay && selectedDay.year === a.year && selectedDay.month === a.month && selectedDay.day === a.day;
    } else if (mode === 'findDayOfWeek') {
      const a = q.answer as DowAnswer;
      ok = selectedDow !== null && selectedDow === a.dayOfWeek;
    } else if (mode === 'findMonthOnly') {
      const a = q.answer as MonthAnswer;
      ok = a.month.includes(visibleMonth);
    }
    if (ok) {
      buttonsBlocked = true;
      ctx.audio.play('correct');
      showFeedback('ok');
      correctCount++;
      if (mode === 'findDay' || mode === 'findYearMonthDay') flashSelected('correct');
      window.setTimeout(() => {
        qIdx++;
        if (qIdx < totalQuestions) startQuestion();
        else finishLevel();
      }, 800);
    } else {
      ctx.audio.play('wrong');
      showFeedback('err');
      if (mode === 'findDay' || mode === 'findYearMonthDay') flashSelected('wrong');
    }
  }

  function flashSelected(kind: 'correct' | 'wrong'): void {
    const cells = gridEl.querySelectorAll<HTMLElement>('.cal__day--selected');
    cells.forEach((c) => {
      c.classList.remove('cal__day--selected');
      c.classList.add(`cal__day--${kind}`);
    });
    window.setTimeout(() => {
      cells.forEach((c) => c.classList.remove(`cal__day--${kind}`));
    }, 800);
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 500);
  }

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctCount);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você acertou ${correctCount} de ${totalQuestions} perguntas.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/calendar/${level.level + 1}`;
      });
    }
  }

  startQuestion();

  return {
    unmount(): void {
      ctx.root.innerHTML = '';
    },
  };
}
